import { useState, useMemo, useEffect } from 'react';
import { X, Search } from 'lucide-react';

/**
 * Metric glossary shown as a slide-in panel from the left sidebar.
 *
 * Definitions + formulas here are the same as those used by the Trace
 * aggregate() helper in FullFunnel.js / AdProperties.js so pill tooltips and
 * this glossary stay in lockstep.
 *
 * Grouped so the sheet feels less like a wall of text — each group is a small
 * collapsible section that expands by default.
 */
const GLOSSARY = [
  { group: 'Spend & Revenue', items: [
    { term: 'Revenue',                formula: 'Σ total_revenue',                                     def: 'Gross sales attributed to the ad activity in the selected date range.' },
    { term: 'Spends (Incl. GST)',     formula: 'ad_spend × 1.18',                                     def: 'Total media spend including 18% GST. All ROI/ACOS calculations use the GST-inclusive spend.' },
    { term: 'Ad Spend',               formula: 'Σ ad_spend',                                          def: 'Media spend before GST. Present in raw uploads.' },
  ]},
  { group: 'Efficiency', items: [
    { term: 'ROI / RoAS',             formula: 'Revenue ÷ Spends (Incl. GST)',                        def: 'Return on Ad Spend. Higher is better — a ROAS of 4.0 means Rs. 4 revenue for every Rs. 1 spent.' },
    { term: 'ACOS',                   formula: '(Spends (Incl. GST) ÷ Revenue) × 100',                def: 'Advertising Cost of Sale. Lower is better — the % of revenue consumed by ad spend.' },
    { term: 'TACoS',                  formula: '(Spends (Incl. GST) ÷ (Revenue × 1.15)) × 100',       def: 'Total ACoS — spend as a % of a broader revenue base (assumes 15% organic uplift). Approximate proxy for total-business impact.' },
  ]},
  { group: 'Volume', items: [
    { term: 'Impressions',            formula: 'Σ views',                                             def: 'Number of times an ad was shown. On marketplaces this is the same underlying field as "Views".' },
    { term: 'Views',                  formula: 'Σ views',                                             def: 'Number of times an ad was displayed or seen. Equivalent to Impressions for most sources.' },
    { term: 'Clicks',                 formula: 'Σ clicks',                                            def: 'Number of times an ad was clicked.' },
    { term: 'Purchases / Units Sold', formula: 'Σ total_converted_units',                             def: 'Units purchased that can be attributed to the ad activity.' },
    { term: 'Reach',                  formula: 'unique users (channel-provided)',                     def: 'Estimated unique users who saw at least one ad. Only where the source platform reports it.' },
  ]},
  { group: 'Rates', items: [
    { term: 'CTR',                    formula: '(Clicks ÷ Views) × 100',                              def: 'Click-Through Rate — % of impressions that resulted in a click.' },
    { term: 'CVR',                    formula: '(Purchases ÷ Clicks) × 100',                          def: 'Conversion Rate — % of clicks that resulted in a purchase.' },
    { term: 'Conversion Rate',        formula: 'same as CVR',                                         def: 'Alias for CVR. Displayed on the Overview scorecards.' },
  ]},
  { group: 'Unit costs', items: [
    { term: 'CPV',                    formula: 'Spends (Incl. GST) ÷ Views',                          def: 'Cost per View. Lower is better.' },
    { term: 'CPM',                    formula: '(Spends (Incl. GST) ÷ Views) × 1000',                 def: 'Cost per 1,000 impressions. Standard rate metric for awareness campaigns.' },
    { term: 'CPC',                    formula: 'Spends (Incl. GST) ÷ Clicks',                         def: 'Cost per Click.' },
    { term: 'AOV',                    formula: 'Revenue ÷ Purchases',                                 def: 'Average Order Value — average revenue per conversion.' },
  ]},
  { group: 'Funnel & mapping', items: [
    { term: 'Awareness',              formula: 'Σ (ad_spend × _sa/100), Σ (revenue × _ra/100)',       def: 'Top-of-funnel bucket. Each mapped campaign contributes weighted by its Awareness split % set in Funnel Mapping.' },
    { term: 'Conversion',             formula: 'Σ (ad_spend × _sc/100), Σ (revenue × _rc/100)',       def: 'Bottom-of-funnel bucket. Weighted by each campaign\'s Conversion split %.' },
    { term: 'Unmapped',               formula: 'campaigns without a funnel-mapping',                  def: 'Campaigns that have not been assigned Awareness/Conversion splits yet. Aggregated at 100% into this bucket.' },
  ]},
  { group: 'Data availability', items: [
    { term: 'Date-wise',              formula: 'any (year-month) has ≥ 2 distinct dates',             def: 'The property reports one row per day. Included in the cumulative line chart.' },
    { term: 'Week-wise',              formula: 'span is 4-20 days and ≤ 3 rows',                      def: 'The property reports one row per week. Excluded from the daily line chart; totals still appear in the table.' },
    { term: 'Month-wise',             formula: 'single row per month, span > 20 days',                def: 'The property reports one aggregated row per month. Excluded from the daily line chart; totals still appear in the table.' },
    { term: 'Mixed',                  formula: 'some months are date-wise, others are not',           def: 'Different months report at different granularities. Only date-wise months contribute to the daily chart.' },
  ]},
];

export default function GlossarySheet({ open, onClose }) {
  const [q, setQ] = useState('');

  // Close on Esc for accessibility.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!q.trim()) return GLOSSARY;
    const needle = q.trim().toLowerCase();
    return GLOSSARY.map(g => ({
      ...g,
      items: g.items.filter(i =>
        i.term.toLowerCase().includes(needle) ||
        i.def.toLowerCase().includes(needle) ||
        i.formula.toLowerCase().includes(needle)
      ),
    })).filter(g => g.items.length > 0);
  }, [q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="glossary-sheet">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
      />
      {/* Panel */}
      <div className="relative ml-auto w-full max-w-[560px] h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-[#0F0F0F]">Metric Glossary</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">Definitions & formulas for every metric used in Trace.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
            data-testid="glossary-close-btn"
            aria-label="Close glossary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search metric or definition…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] focus:ring-2 focus:ring-[#32B4AC]/10"
              data-testid="glossary-search-input"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">No matches for "{q}".</p>
          )}
          {filtered.map(group => (
            <section key={group.group}>
              <h3 className="text-[10.5px] font-bold uppercase tracking-wider text-gray-500 mb-2">{group.group}</h3>
              <div className="space-y-2">
                {group.items.map(item => (
                  <div key={item.term} className="border border-gray-100 rounded-xl p-3 hover:border-gray-200 hover:bg-gray-50/40 transition-colors" data-testid={`glossary-item-${item.term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <h4 className="text-sm font-semibold text-[#0F0F0F]">{item.term}</h4>
                      <code className="text-[11px] text-[#0F766E] bg-[#E6F7F5] px-1.5 py-0.5 rounded font-mono">{item.formula}</code>
                    </div>
                    <p className="text-[12.5px] text-gray-600 mt-1 leading-relaxed">{item.def}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
